import { apiClient } from '@boothiva/api-client';

export type TemplateStatus = 'Draft' | 'Published' | 'Archived';
export type TemplateOrientation = 'portrait' | 'landscape';
export type TemplatePrintSizeCode = '4x6' | '5x7' | '6x8' | '2x6' | 'custom';
export type PresetTemplatePrintSizeCode = Exclude<TemplatePrintSizeCode, 'custom'>;

export type TemplateRecord = {
  id: string;
  name: string;
  description: string | null;
  status: TemplateStatus;
  printSizeCode: TemplatePrintSizeCode;
  orientation: TemplateOrientation;
  dpi: number;
  photoSlots: number;
  publishedVersionNumber: number | null;
  draftDefinition: unknown;
  thumbnailStorageKey: string | null;
  lastPublishedAtUtc: string | null;
  version: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
};

export type TemplatePage = {
  items: TemplateRecord[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export type TemplateVersionSummary = {
  versionNumber: number;
  definitionHash: string;
  schemaVersion: number;
  previewStorageKey: string | null;
  publishedByUserId: string;
  publishedAtUtc: string;
  notes: string | null;
  isCurrent: boolean;
};

export type TemplateFilters = {
  page: number;
  pageSize: number;
  status?: string;
  printSize?: string;
  orientation?: string;
};

export type CreateTemplateRequest = {
  name: string;
  printSizeCode: PresetTemplatePrintSizeCode;
  orientation: TemplateOrientation;
};

export type TemplateRecordWithEtag = {
  record: TemplateRecord;
  etag: string;
};

const requireEtag = (response: Response): string => {
  const etag = response.headers.get('ETag');
  if (!etag) throw new Error('The template response did not include the required ETag header.');
  return etag;
};

export function listTemplates(filters: TemplateFilters) {
  const query = new URLSearchParams({ page: String(filters.page), pageSize: String(filters.pageSize) });
  if (filters.status) query.set('status', filters.status);
  if (filters.printSize) query.set('printSize', filters.printSize);
  if (filters.orientation) query.set('orientation', filters.orientation);
  return apiClient.get<TemplatePage>(`/api/v1/templates?${query}`);
}

export const createTemplate = (request: CreateTemplateRequest) => apiClient.post<TemplateRecord>('/api/v1/templates', request);
export async function getTemplate(templateId: string): Promise<TemplateRecordWithEtag> {
  const { data: record, response } = await apiClient.getWithResponse<TemplateRecord>(`/api/v1/templates/${templateId}`);
  return { record, etag: requireEtag(response) };
}

export async function saveTemplateDraft(templateId: string, definition: unknown, ifMatch: string): Promise<TemplateRecordWithEtag> {
  const { data: record, response } = await apiClient.putWithResponse<TemplateRecord>(
    `/api/v1/templates/${templateId}/draft`,
    { definition },
    { 'If-Match': ifMatch },
  );
  return { record, etag: requireEtag(response) };
}
export const duplicateTemplate = (templateId: string) => apiClient.post<TemplateRecord>(`/api/v1/templates/${templateId}/duplicate`);
export const archiveTemplate = (templateId: string) => apiClient.post<TemplateRecord>(`/api/v1/templates/${templateId}/archive`);
export const deleteTemplate = (templateId: string) => apiClient.delete<void>(`/api/v1/templates/${templateId}`);
export const listTemplateVersions = (templateId: string) => apiClient.get<TemplateVersionSummary[]>(`/api/v1/templates/${templateId}/versions`);
export const publishTemplate = (templateId: string) => apiClient.post<TemplateVersionSummary>(`/api/v1/templates/${templateId}/publish`);
